<script>
    import {_, locale, number} from 'svelte-i18n';
    import Ribbon from '../lib/Ribbon.svelte';
    import Starburst from '../lib/Starburst.svelte';
    import Countdown from '../lib/Countdown.svelte';
    import {gameConfig, selectedCurrency, user, giftFriend, luckyDrawEvent, scrollToPackId} from '../stores';
    import {getStorePackConfig} from '../yaml';
    import {amazon_pack_image_url, game_name, validate_payment_route, ENABLE_PACK_PREVIEW_SCROLL} from '../constants';
    import ItemsListing from './ItemsListing.svelte';
    import {getPackRarity, resourceDetails} from '../globals';
    import {isQuote} from './pack-pricing.js';
    import giftPackIcon from '../assets/gift_friend/gift_pack_icon.png?format=webp;jpg';
    import giftPackWinterIcon from '../assets/gift_friend/winter_gift_pack_icon.png?format=webp;jpg';
    import SnowDecoration from "./SnowDecoration.svelte";
    import { autoScrollY } from './actions/autoScrollY.js';

    let { storePack = {}, storeSection = '', globals = {}, onbuyPack, compact = false, onOpenDetail } = $props();

    // Check if this is a free pack
    let isFreePack = $derived(storePack.isFreePack || !storePack.paymentHub?.price);
    let processing = $state(false);
    let animate = $state(false);
    let packEl = $state(null);
    // svelte-ignore state_referenced_locally
    let freePackCollected = $state(storePack.collected || false);

    let packDetails = $state(null);
    // rarity: 1 = common, 2 = rare, 3 = epic, 4 = legendary
    let rarity = $derived(storePack.rarity);
    let goldBonus = $derived(storePack.goldBonus || '');
    let goldWorth = $derived(storePack.goldWorth || '');
    let goldAmount = $derived(
        storeSection === 'subscriptions'
            ? storePack.dailyRewards.gold
            : storePack.gold || '');
    let items = $derived(
        storeSection === 'subscriptions'
            ? storePack.dailyRewards.items
            : storeSection === 'giftFriend'
                ? [...storePack.itemsForAFriend, ...storePack.items]
                : storePack.bonusRewards?.length > 0
                    ? storePack.bonusRewards
                    : storePack.items || []);
    let giftPackIconUrl = giftPackIcon;
    let giftPackWinterIconUrl = giftPackWinterIcon;

    let priceString = $derived(!isFreePack && storePack.paymentHub?.price && storePack.paymentHub.price.amount !== 'missing' && storePack.paymentHub.price.currency !== 'missing'
        ? $number(storePack.paymentHub.price.amount / 100, {
            style: 'currency',
            currency: storePack.paymentHub.price.currency,
        })
        : isFreePack ? 'Free' : 'Price not available');

    // Compact-card support (mobile). The strikethrough only shows when there is a real discount.
    let hasDiscount = $derived(
        isQuote(storePack.paymentHub?.fullPrice) && isQuote(storePack.paymentHub?.price)
        && storePack.paymentHub.fullPrice.amount !== storePack.paymentHub.price.amount
    );
    let fullPriceString = $derived(hasDiscount
        ? $number(storePack.paymentHub.fullPrice.amount / 100, {
            style: 'currency',
            currency: storePack.paymentHub.fullPrice.currency,
        })
        : '');

    // Mirror the desktop buy-button discount badge for the compact card: the red
    // lucky-draw badge image, hung bottom-left of the price row. Same selection
    // logic as the desktop markup (lucky-draw event discount, then per-pack
    // webshop discount, else the default 10% badge). Hidden for free/gift packs.
    let explicitNoDiscount = $derived(
        storePack.webshopDiscount === 0 && !(storeSection === 'luckyDraw' && $luckyDrawEvent?.availableDiscount)
    );
    let discountBadgeSrc = $derived.by(() => {
        if (isFreePack || storeSection === 'giftFriend' || explicitNoDiscount) return '';
        if (storeSection === 'luckyDraw' && $luckyDrawEvent?.availableDiscount) {
            return `/images/lucky_draw/badge/red_discount_${$luckyDrawEvent.availableDiscount}.png`;
        }
        if (storePack.webshopDiscount > 0 && storeSection !== 'luckyDraw') {
            return `/images/lucky_draw/badge/red_discount_${storePack.webshopDiscount}.png`;
        }
        return '/images/lucky_draw/badge/red_discount_10.png';
    });
    let imageSrc = $derived.by(() => {
        if (!packDetails) return '';
        if (packDetails.packImage) return packDetails.packImage;
        const side = packDetails.imageSide ?? '';
        if (storeSection === 'promos' || storeSection === 'beginnerPacks' || storeSection === 'featured') {
            return `${amazon_pack_image_url}/${storePack.bundleName}/image${side}.jpg`;
        }
        return `${amazon_pack_image_url}/${storePack.bundleName}/image.jpg`;
    });

    function openDetail() {
        onOpenDetail?.({
            storePack,
            storeSection,
            packDetails,
            items,
            goldAmount,
            goldWorth,
            priceString,
            fullPriceString,
            imageSrc,
            globals,
            discountBadgeSrc,
            rarityClass: (storeSection !== 'resources' && storeSection !== 'goldPacks') ? packRarity : '',
            // Bonus/discount starburst, mirroring the card's badge conditions so
            // the detail sheet shows the same badge the card advertised.
            badge: (storeSection !== 'goldAndResources' && storeSection !== 'goldRush')
                ? (storePack.showBonus
                    ? { label: 'Bonus', text: $number(Math.floor((goldWorth + goldAmount + goldBonus) / (goldAmount + goldBonus)) * 100) + '%' }
                    : (storePack.discountValue ? { label: 'Discount', text: '-' + storePack.discountValue + '%' } : null))
                : null,
        });
    }

    // function getUnityPackConfig() {
    //     if (storePack.bundleName !== undefined) {
    //         return getStorePackConfig(
    //             amazon_pack_image_url + '/' + storePack.bundleName + '/config.asset',
    //         );
    //     }
    // }

    function formatTimeFriendly(seconds) {
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        let days = Math.floor(hours / 24);

        let timeString = `${minutes} ${$_('Minute')}`;

        if (seconds - 1 > 60 * 60 * 24) {
            timeString = `${days} ${$_('Days')}`;
        } else if (seconds > 60 * 60) {
            timeString = `${hours} ${$_('Hours')}`;
        } else if (seconds >= 60) {
            timeString = `${minutes} ${$_('Minutes')}`;
        }

        return timeString;
    }

    async function getPromoPackDetails() {
        let details = {};
            if (storePack['titleNorthKey'] === undefined) {
                details.title = storePack['specialTitle'];
                // details.description = config['specialTitleDescriptionKey'];
                details.imageSide = '';
            } else {
                if ($user.user.side === 1) {
                    details.title = storePack['titleNorthKey'];
                    // details.description = config['descriptionNorthKey'];
                } else {
                    details.title = storePack['titleSouthKey'];
                    // details.description = config['descriptionSouthKey'];
                }

                details.imageSide = $user.user.side;
            }

            details.showOneTimeOnly = storePack['isOneTimeOnly'];
            details.showTimer = storePack['showTimer'];

        return details;
    }

    async function getDailyDealPackDetails() {
        let details = {};

        details.showOneTimeOnly = storePack['isOneTimeOnly'];
        details.showTimer = storePack['showTimer'];

        return details;
    }

    async function displayStoreSection() {
        // Snapshot $derived values synchronously before any await — when the
        // parent $effect re-runs (e.g. on $locale resolve) or the component
        // unmounts mid-fetch, Svelte 5 inertizes derived state and returns a
        // Symbol sentinel that crashes $number() inside async .then() callbacks.
        const snapGoldAmount = goldAmount;
        const snapGoldWorth = goldWorth;

        switch (storeSection) {
            case 'promos':
            case 'luckyDraw':
            case 'beginnerPacks':
            case 'welcomeBackPacks':
            case 'midweekMadness':
            case 'vip':
            case 'featured':
            case 'webshopExclusive':
            case 'customizablePacks':
                await getPromoPackDetails().then((data) => {
                    packDetails = data;
                    packDetails.description = $_('PacksDescription', {
                        values: {
                            GOLD_VALUE: $number(snapGoldWorth),
                        },
                    });
                    packDetails.subTitle = `${$_('PacksReceiveGold')} ${$number(
                        snapGoldAmount,
                    )} ${$_('Gold')}`;
                });

                break;
            case 'battlePass':
                packDetails = {
                    title: storePack.specialTitle ? storePack.specialTitle : 'BattlePassEventTitle',
                    subTitle: 'GrindThanksGivingUnlockTitle',
                    description: 'BattlePassPurchaseInfo1Title',
                    packImage: '/images/packImages/battlepass-generic-event.jpg',
                };

                break;
            case 'eventImagePacks': {
                const receiveGoldText = `${$_('PacksReceiveGold')} ${$number(snapGoldAmount)} ${$_('Gold')}`;
                const worthDescription = $_('PacksDescription', {
                    values: { GOLD_VALUE: $number(snapGoldWorth) },
                });
                if (storePack.bundleName?.startsWith('fortunepack')) {
                    packDetails = {
                        title: storePack.specialTitle || 'Fortune Teller',
                        subTitle: receiveGoldText,
                        description: worthDescription,
                        packImage: '/images/packImages/fortunetellerpack.jpg',
                    };
                } else if (storePack.bundleName?.startsWith('wheeloffortune')) {
                    packDetails = {
                        title: storePack.specialTitle || 'Wheel of Fortune',
                        subTitle: receiveGoldText,
                        description: worthDescription,
                        packImage: '/images/packImages/wheeloffortunepack.png',
                    };
                }

                break;
            }
            case 'specialEvents':
                await getPromoPackDetails().then((data) => {
                    packDetails = data;
                    const receiveGoldText = `${$_('PacksReceiveGold')} ${$number(snapGoldAmount)} ${$_('Gold')}`;
                    const worthDescription = $_('PacksDescription', {
                        values: {
                            GOLD_VALUE: $number(snapGoldWorth),
                        },
                    });
                    packDetails.description = `${receiveGoldText} ${worthDescription}`;
                    packDetails.subTitle = receiveGoldText;
                });

                break;
            case 'cardCollection':
                await getPromoPackDetails().then((data) => {
                    packDetails = data;
                    // Backend ships some title keys with a doubled trailing 'Title'.
                    // Drop one so a single 'Title' suffix remains for the i18n lookup.
                    if (typeof packDetails.title === 'string' && packDetails.title.endsWith('TitleTitle')) {
                        packDetails.title = packDetails.title.slice(0, -'Title'.length);
                    }
                    packDetails.subTitle = `${$_('PacksReceiveGold')} ${$number(snapGoldAmount)} ${$_('Gold')}`;
                    packDetails.description = $_('PacksDescription', {
                        values: {
                            GOLD_VALUE: $number(snapGoldWorth),
                        },
                    });
                    packDetails.packImage = '/images/packImages/cardcollectionpack.jpg';
                });

                break;
            case 'pvpArena':
                await getPromoPackDetails().then((data) => {
                    packDetails = data;
                    packDetails.subTitle = `${$_('PacksReceiveGold')} ${$number(snapGoldAmount)} ${$_('Gold')}`;
                    packDetails.description = $_('PacksDescription', {
                        values: {
                            GOLD_VALUE: $number(snapGoldWorth),
                        },
                    });
                    packDetails.packImage = '/images/packImages/dividednationspack.jpg';
                });

                break;
            case 'singleJackFever':
                await getPromoPackDetails().then((data) => {
                    packDetails = data;
                    packDetails.subTitle = `${$_('PacksReceiveGold')} ${$number(snapGoldAmount)} ${$_('Gold')}`;
                    packDetails.description = $_('PacksDescription', {
                        values: {
                            GOLD_VALUE: $number(snapGoldWorth),
                        },
                    });
                    packDetails.packImage = '/images/packImages/singlejackfeverpack.jpg';
                });

                break;
            case 'dailyDeals':
                await getPromoPackDetails().then((data) => {
                    packDetails = data;
                    packDetails.description = $_('PacksDescription', {
                        values: {
                            GOLD_VALUE: $number(snapGoldWorth),
                        },
                    });
                    packDetails.subTitle = `${$_('PacksReceiveGold')} ${$number(
                        snapGoldAmount,
                    )} ${$_('Gold')}`;
                });

                break;

            case 'goldRush':
                packDetails = {
                    title: $_('NewShopTabEventRecharge'),
                    description: $_('NewShopTabEventRecharge'),
                    subTitle: $_('NewShopTabEventRecharge'),
                };
                break;

            case 'goldPacks':
                await getPromoPackDetails().then((data) => {
                    const isPrimetime = storePack.specialTitle && storePack.specialTitle.toLowerCase().includes('primetime');
                    packDetails = data;
                    packDetails.subTitle = $_(packDetails.title);
                    packDetails.title = `${$number(snapGoldAmount)} Gold`;
                    packDetails.description = $_('PacksDescription', {
                        values: {
                            GOLD_VALUE: $number(snapGoldWorth),
                        },
                    });
                });
                break;

            case 'resources':
                packDetails = {
                    title:
                        storePack.specialTitle.replace('subscription', 'Subscription') +
                        'Title',
                    description:
                        storePack.specialTitleDescription.replace(
                            'subscription',
                            'Subscription',
                        ) + 'Description',
                    subTitle:
                        storePack.specialTitle.replace('subscription', 'Subscription') +
                        'Title',
                };
                break;

            case 'giftFriend':
                if (storePack.holidayTheme === 'winter') {
                    packDetails = {
                        title: $_('GiftPackWinter'),
                        description: $_('GiftPackDescriptionWinter', {
                            values: {
                                AMOUNT: priceString,
                            },
                        }),
                        subTitle: $_('GiftPackWinter'),
                    };
                } else {
                    packDetails = {
                        title: priceString + ' ' + $_('GiftPack'),
                        description: storePack.itemsForAFriend[0].amount > 1 ? $_('GiftPackMultipleDescription') : $_('GiftPackDescription'),
                        subTitle: priceString + ' ' + $_('GiftPack'),
                    };
                }
                break;

            default:
                packDetails = {
                    title: storePack.specialTitle,
                    description: storePack.specialTitleDescription,
                    subTitle: storePack.specialTitle + 'Title',
                };
        }
    }

    function buyPack() {
        onbuyPack?.({
            storePack: storePack,
            packDetails: packDetails,
        });
    }

    async function collectFreePack() {
        try {
            processing = true;
            const params = {
                packId: storePack.internalId,
                productId: storePack.internalId,
                specialStore: "freePack",
                isFreePack: true,
                price: 0,
            };

            const response = await fetch(validate_payment_route, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(params),
            });

            if (!response.ok) {
                const message = `An error has occured: ${response.status}`;
                throw new Error(message);
            }

            const res = await response.text();
            animate = true;
            freePackCollected = true;

        } catch (error) {
            Rollbar.error('Error collecting free pack: ' + error);
        } finally {
            processing = false;
        }
    }

    function setActiveGiftFriendPack() {
        giftFriend.set(
            {
                startPurchase: false,
                selectedPack: storePack,
                selectedPackDetails: packDetails,
                message: '',
            });
    }

    giftFriend.subscribe(value => {
        if (value.startPurchase) {
            onbuyPack?.({
                storePack: $giftFriend.selectedPack,
                packDetails: $giftFriend.selectedPackDetails,
            });
        }
    });

    $effect(() => {
        $locale;
        displayStoreSection();
    });

    $effect(() => {
        if ($scrollToPackId === storePack.internalId && packEl) {
            scrollToPackId.set(null);
            setTimeout(() => {
                packEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setTimeout(() => {
                    packEl?.classList.add('animate__animated', 'animate__rubberBand');
                    packEl?.addEventListener('animationend', () => {
                        packEl?.classList.remove('animate__animated', 'animate__rubberBand');
                    }, { once: true });
                }, 500);
            }, 0);
        }
    });

    // svelte-ignore state_referenced_locally
    globals.resourceDetails = resourceDetails;
    // svelte-ignore state_referenced_locally
    globals.formatTimeFriendly = formatTimeFriendly;
    let packRarity = $derived(getPackRarity(rarity));
</script>

{#if $gameConfig?.gameItems}
    {#await $gameConfig.gameItems then gameItems}
        {#if packDetails}
            {#if compact}
            <button type="button" class="pack-compact {storeSection !== 'resources' && storeSection !== 'goldPacks' ? packRarity : ''} {storeSection}" onclick={openDetail}>
                {#if packDetails.showOneTimeOnly}
                    <Ribbon>{$_('OneTimeOnlyPromoText')}</Ribbon>
                {/if}
                {#if storeSection !== 'goldAndResources' && storeSection !== 'goldRush'}
                    {#if storePack.showBonus}
                        <Starburst>
                            {$number(Math.floor((goldWorth + goldAmount + goldBonus) / (goldAmount + goldBonus)) * 100)}%
                        </Starburst>
                    {:else if storePack.discountValue}
                        <Starburst label="Discount">
                            <div>-{storePack.discountValue}%</div>
                        </Starburst>
                    {/if}
                {/if}
                {#if storePack.showPlayerName}
                    <div class="pc-exclusive">{$_('Exclusive for')} {$user ? $user.user.username : $_('Player')}</div>
                {/if}
                <div class="pc-art">
                    {#if imageSrc}
                        <img src={imageSrc} alt={$_(packDetails.title)} loading="lazy"/>
                    {/if}
                    <!-- Desktop-style layout: the title sits on the art. -->
                    <div class="pc-arttitle">
                        <h3 class="pc-title">{@html $_(packDetails.title)}</h3>
                    </div>
                </div>
                {#if goldAmount > 0 && storeSection !== 'goldPacks'}
                    <div class="pc-rewards pc-rewards-band">
                        <span class="pc-rewards-gold">{$_('PacksReceiveGold')} {$number(goldAmount)} {$_('Gold')}</span>
                        {#if goldWorth > 0}
                            <span class="pc-rewards-worth">{@html $_('PacksDescription', { values: { GOLD_VALUE: $number(goldWorth) } })}</span>
                        {/if}
                    </div>
                {/if}
                {#if storeSection !== 'goldPacks'}
                    <div class="pc-items">
                        <ItemsListing storePack={storePack} goldAmount={goldAmount} globals={globals} items={items} hasMask={false} scrollX={ENABLE_PACK_PREVIEW_SCROLL}/>
                    </div>
                {/if}
                {#if storePack.endAt}
                    <Countdown endDate={storePack.endAt}>
                        {$_('IncrementalPacksTimeLeft')}
                    </Countdown>
                {/if}
                <div class="pc-body">
                    {#if discountBadgeSrc}
                        <img class="pc-discount-badge" alt="Discount" src={discountBadgeSrc}/>
                    {/if}
                    <span class="pc-prices">
                        {#if fullPriceString}<s class="pc-was">{fullPriceString}</s>{/if}
                        <span class="pc-price">{priceString}</span>
                    </span>
                </div>
            </button>
            {:else}
            <div bind:this={packEl} id="pack-{storePack.internalId}" class="pack {storeSection !== 'resources' && storeSection !== 'goldPacks' ? packRarity: ''} {storeSection}">
                <!--                <img class="snow1" src="/images/snow.png" alt=""/>-->
                <!--                 {console.log(packDetails)} -->
                {#if packDetails.showOneTimeOnly}
                    <Ribbon>{$_('OneTimeOnlyPromoText')}</Ribbon>
                {/if}
                {#if storeSection !== 'goldAndResources' && storeSection !== 'goldRush'}
                    {#if storePack.showBonus}
                        <Starburst>
                            {$number(Math.floor((goldWorth + goldAmount + goldBonus) / (goldAmount + goldBonus)) * 100)}%
                        </Starburst>
                    {:else if storePack.discountValue}
                        <Starburst label="Discount">
                            <div>-{storePack.discountValue}%</div>
                        </Starburst>
                    {/if}
                {/if}
                {#if storePack.showPlayerName}
                    <div class="exclusive-player-tag">
                        {$_('Exclusive for')} {$user ? $user.user.username : $_('Player')}
                    </div>
                {/if}
                <div class="content">
                    {#if storeSection === 'goldPacks' && storePack.specialTitle}
                        <div class="gold-pack-title">
                            <h3>{$_(packDetails.subTitle)}</h3>
                        </div>
                    {/if}
                    {#if storeSection !== 'subscriptions' && storeSection !== 'giftFriend' && !(storeSection === 'specialEvents' && !packDetails.packImage)}
                        <div class="header">
                            {#if storePack.purchased}
                                <h2>{$_('Purchased')}</h2>
                            {/if}
                                <h2 class="{storeSection == 'goldPacks' ? 'gold_l': ''}">
                                    {#if storeSection === 'goldPacks'}
                                        {@html $_(packDetails.title).replace(' Gold', ' <span class="sr-only">Gold</span>')}
                                    {:else}
                                        {$_(packDetails.title)}
                                    {/if}
                                </h2>
                            <span class="shadow"></span>
                            {#if packDetails.packImage}
                                <img
                                        class="pack-image"
                                        src={packDetails.packImage}
                                        alt={$_(packDetails.title)}
                                />
                            {:else if storeSection === 'promos' || storeSection === 'beginnerPacks' || storeSection === 'featured'}
                                <picture>
                                    <source srcset="{amazon_pack_image_url}/{storePack.bundleName}/image{packDetails.imageSide}.webp"
                                            type="image/webp">
                                    <source srcset="{amazon_pack_image_url}/{storePack.bundleName}/image{packDetails.imageSide}.jpg"
                                            type="image/jpeg">
                                    <img
                                            class="pack-image"
                                            src="{amazon_pack_image_url}/{storePack.bundleName}/image{packDetails.imageSide}.jpg"
                                            alt={$_(packDetails.title)}
                                    />
                                </picture>
                            {:else}
                                <picture>
                                    <source srcset="{amazon_pack_image_url}/{storePack.bundleName}/image.webp"
                                            type="image/webp">
                                    <source srcset="{amazon_pack_image_url}/{storePack.bundleName}/image.jpg"
                                            type="image/jpeg">
                                    <img
                                            class="pack-image"
                                            src="{amazon_pack_image_url}/{storePack.bundleName}/image.jpg"
                                            alt={$_(packDetails.title)}
                                    />
                                </picture>
                            {/if}
                        </div>
                    {/if}
                    {#if storeSection !== 'goldPacks'}
                      <div class="sub-header" use:autoScrollY>
                          {#if storeSection === 'giftFriend'}
                              {#if storePack.holidayTheme === 'winter'}
                                  <picture class="gift-pack-icon">
                                      <source srcset={giftPackWinterIconUrl[0]} type="image/webp">
                                      <source srcset={giftPackWinterIconUrl[1]} type="image/jpg">
                                      <img alt="Winter Gift Pack Icon" src={giftPackWinterIconUrl[0]} width="150px">
                                  </picture>
                              {:else}
                                  <picture class="gift-pack-icon">
                                      <source srcset={giftPackIconUrl[0]} type="image/webp">
                                      <source srcset={giftPackIconUrl[1]} type="image/jpg">
                                      <img alt="Gift Pack Icon" src={giftPackIconUrl[0]} width="150px">
                                  </picture>
                              {/if}
                          {/if}
                          {#if storeSection === 'specialEvents' && !packDetails.packImage}
                              <h3>{$_(packDetails.title)}</h3>
                              <p>
                                  {@html packDetails.description}
                              </p>
                          {:else}
                              <h3 class="{storePack.extraGold ? 'extraGold' : ''}">{$_(packDetails.subTitle)}</h3>
                              {#if storePack.extraGold}
                                  <div class="extraGold animate__animated animate__shakeY animate__delay-2s">
                                      <span>+{$number(storePack.gold)} {$_('more')}</span>
                                  </div>
                              {/if}
                              {#if storeSection === 'resources'}
                                  <h4>
                                      {`${$_('PacksReceiveGold')} ${$number(goldAmount)} ${$_('Gold')}`}
                                  </h4>
                                  <span class="shadow"></span>
                                  <small>
                                      {$_('NewShopValidFor')}
                                      {storePack.dailyRewards.days}
                                      {$_('Days')}
                                  </small>
                              {/if}

                              {#if storePack.storeSection !== 'goldPacks' || storePack.goldWorth}
                                  <p>
                                      {@html $_(packDetails.description, {
                                          values: {GOLD_VALUE: $number(goldWorth)},
                                      })}
                                  </p>
                              {/if}
                          {/if}
                      </div>
                    {/if}
                    <ItemsListing storePack={storePack} goldAmount={goldAmount} globals={globals} items={items}/>

                </div>
                {#if storePack.endAt}
                    <Countdown endDate={storePack.endAt}>
                        {$_('IncrementalPacksTimeLeft')}
                    </Countdown>
                {/if}

                {#if storeSection === 'goldPacks' && storePack.specialTitle && storePack.specialTitle.toLowerCase().includes('doublegoldpack')}
                    <div class="double-gold-info">
                        Double-Up Deal (First Buy Only!)
                    </div>
                {/if}

                {#if isFreePack}
                    {#if freePackCollected}
                        <button class="button primary buy-button green" disabled>
                            <SnowDecoration/>
                            {$_('Collected')}
                        </button>
                    {:else}
                        <button class="button primary buy-button green" class:loading={processing} disabled={processing} onclick={collectFreePack}>
                            <SnowDecoration/>
                            {$_('Collect')}
                        </button>
                    {/if}
                {:else if storeSection !== 'giftFriend'}
                    {@const explicitNoDiscount = storePack.webshopDiscount === 0 && !(storeSection === 'luckyDraw' && $luckyDrawEvent.availableDiscount)}
                    <button class="button primary buy-button orange" onclick={buyPack}>
                        <SnowDecoration/>
                        {#if !explicitNoDiscount}
                            {#if storeSection === 'luckyDraw' && $luckyDrawEvent.availableDiscount}
                                <img alt="Discount" class="lucky-draw-discount"
                                     src="/images/lucky_draw/badge/red_discount_{$luckyDrawEvent.availableDiscount}.png"/>
                            {:else if storePack.webshopDiscount > 0 && storeSection !== 'luckyDraw'}
                                <img alt="Discount" class="lucky-draw-discount"
                                     src="/images/lucky_draw/badge/red_discount_{storePack.webshopDiscount}.png"/>
                            {:else}
                                <img alt="Discount" class="lucky-draw-discount"
                                     src="/images/lucky_draw/badge/red_discount_10.png"/>
                            {/if}
                            {#if storePack.paymentHub?.fullPrice && storePack.paymentHub.fullPrice.amount !== 'missing' && storePack.paymentHub.fullPrice.currency !== 'missing'}
                                <s>
                                    {$number(
                                        storePack.paymentHub.fullPrice.amount / 100,
                                        {
                                            style: 'currency',
                                            currency: storePack.paymentHub.fullPrice.currency,
                                        },
                                    )}
                                </s>
                            {:else}
                                Price or currency missing
                            {/if}
                        {/if}
                        {#if storePack.paymentHub?.price && storePack.paymentHub.price.amount !== 'missing' && storePack.paymentHub.price.currency !== 'missing'}
                            {$number(storePack.paymentHub.price.amount / 100, {
                                style: 'currency',
                                currency: storePack.paymentHub.price.currency,
                            })}
                        {:else}
                            Price or currency missing
                        {/if}
                    </button>
                {:else}
                    <button class="button primary buy-button orange" onclick={setActiveGiftFriendPack}>
                        <SnowDecoration/>
                        {#if storePack.paymentHub?.price && storePack.paymentHub.price.amount !== 'missing' && storePack.paymentHub.price.currency !== 'missing'}
                            {$number(storePack.paymentHub.price.amount / 100, {
                                style: 'currency',
                                currency: storePack.paymentHub.price.currency,
                            })}
                        {:else}
                            Price or currency missing
                        {/if}
                    </button>
                {/if}
            </div>
            {/if}
        {/if}
    {:catch e}
        Error resolving promise
    {/await}
{/if}

<style lang="scss">
  @use 'sass:color';
  @use '../styles/vars.scss' as *;

  @use '../styles/rarity.scss' as rar;
  @use '../styles/compact-card.scss' as compact;

  :global {
    .missing {
      border: 2px solid greenyellow;
      background-color: black;
      color: greenyellow;
      font-family: monospace;
      font-size: 12px;
      padding: 5px;
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
    }

    .content {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
    }

    .pack {
      background-color: black;
      padding: 15px 10px 15px 10px;
      margin: auto;
      height: 100%;
      width: 100%;
      color: white;
      position: relative;
      text-align: left;
      display: flex;
      justify-content: space-between;
      flex-direction: column;
      box-shadow: inset 0px 0px 10px 3px rgb(0 0 0 / 5%);
      border-radius: $store-pack-border-radius;
      z-index: 1;

      .exclusive-player-tag {
        position: absolute;
        top: -30px;
        left: 0;
        background-color: #28a745;
        color: white;
        padding: 3px 6px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 600;
        z-index: 20;
        font-family: var(--font-family-sans-condensed);
        text-align: center;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }

      .lucky-draw-discount {
        position: absolute;
        left: 5px;
        bottom: -17px;
        width: 55px;
        transform: rotate(-10deg);
      }

      .timer {
        text-align: center !important;
        display: inline-block;
        font-size: 12px;
        line-height: 12px;
        font-weight: 700;
        color: rgba(0, 0, 0, 0.7);
        text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
        transition: opacity 0.4s;
        margin-bottom: -12px;
      }

      &.goldPacks {
        background: #ffdc6a;
        border: $store-pack-border-width solid #f7e3ad;
        height: 240px;

        .gold-pack-title {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          z-index: 50;
          text-align: center;
          padding: 5px 10px;
          max-width: 85%;
          justify-self: center;

          h3 {
            font-size: 16px;
            line-height: 18px;
            color: #fff;
            text-shadow: 0 0 6px rgba(255, 200, 0, 1),
                         0 0 8px rgba(255, 200, 0, 0.9),
                         0 0 10px rgba(255, 200, 0, 0.7),
                         0 1px 2px rgba(0, 0, 0, 0.5);
            padding: 0;
            margin: 0;
            font-weight: 700;
            font-family: var(--font-family-sans-condensed);
          }
        }

        .header {
          height: 150px;
          box-shadow: inset 0 1px 18px 0 rgba(255, 200, 0, 0.8);

          .shadow {
            box-shadow: inset 0 5px 15px 0 rgba(255, 200, 0, 0.6),
            inset 0px 1px 0px 0px rgba(255, 200, 0, 0.5);
          }

          h2 {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 3px;
            font-size: 48px;
            bottom: 50px;
            left: 15px;
            color: #ffffff;
            text-shadow: 0 0 30px rgba(255, 200, 0, 1),
                         0 0 40px rgba(255, 200, 0, 0.9),
                         0 0 50px rgba(255, 200, 0, 0.7),
                         0 2px 4px rgba(0, 0, 0, 0.5);
            max-width: none;
            max-height: none;

            &::after {
              width: 40px;
              height: 40px;
            }
          }

          .pack-image {
            height: 180px;
          }
        }

        .items {
          left: 10px;
          bottom: 125px;
        }

        :global(.timer) {
          position: absolute;
          bottom: 13px;
          left: 50%;
          transform: translateX(-50%);
          width: auto;
          text-align: center;
        }

        .double-gold-info {
          position: absolute;
          bottom: 70px;
          left: 50%;
          transform: translateX(-50%);
          text-align: center !important;
          display: inline-block;
          font-size: 12px;
          line-height: 12px;
          font-weight: 700;
          color: rgba(0, 0, 0, 0.7);
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
          transition: opacity 0.4s;
          width: 100%;
        }
      }

      &.resources {
        height: $store-pack-height-resources;
        background: #ffdc6a;
        border: $store-pack-border-width solid #f7e3ad;

        h2 {
          display: none;
        }

        $headerOffset: 60px;

        .sub-header {
          margin-top: -$headerOffset;
          display: flex;
          flex-direction: column;
          justify-content: space-evenly;
          z-index: 10;
          height: $store-pack-height-resources - $store-pack-header-height + $headerOffset;
          border-bottom-left-radius: $store-pack-border-radius;
          border-bottom-right-radius: $store-pack-border-radius;
          background: $legendaryMainColor;
          padding-bottom: 88px;
          background: radial-gradient(
                          ellipse at center,
                          #ffb92d 10%,
                          color.adjust($legendaryMainColor, $saturation: 40%) 100%
          );

          .shadow {
            width: 100%;
            height: 60px;
            position: absolute;
            z-index: 12;
            background-color: rgba(0, 0, 0, 0.2);
            left: 0;
            bottom: 28px;
          }

          small {
            position: absolute;
            width: 100%;
            bottom: 5px;
            left: 0;
          }

          h3 {
            font-size: 22px;
            line-height: 26px;
            color: #531f0d;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.3);
            padding: 10px 0;
          }

          h4 {
            margin: 0;
            font-weight: 700;
            font-family: var(--font-family-sans-condensed);
          }

          p {
            margin-top: 5px;
            color: #531f0d;
            text-shadow: 0 1px 0 rgba(255, 255, 255, 0.3);
          }
        }
      }

      &.giftFriend {
        height: 410px;

        .content {
          width: 100%;

          .sub-header {
            margin-top: -5px;
            height: 100px;
            top: 0;
            overflow: visible;

            .gift-pack-icon {
              display: flex;
              justify-content: center;
              align-items: center;
              position: absolute;
              top: -75px;
            }
          }

          .items {
            margin-top: 75px;

            .mask {
              height: 213px;
            }
          }
        }

        .buy-button {
          height: 52px;
        }
      }

      // Free pack collect button styling
      .buy-button.green {
        height: 52px;
      }

      &.specialEvents {
        height: $store-pack-height-subscriptions;

        .starburst {
          right: -16px;
          top: -28px;
        }

        .sub-header {
          margin-top: -5px;
          height: 100px;
          top: 0;
          overflow: hidden;
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.3);
        }

        h3 {
          font-family: var(--font-family-sans-condensed);
          font-size: 17px;
          line-height: 24px;
          font-weight: 700;
          margin: 0;
          color: #feffd6;
        }

        p {
          font-size: 12px;
          font-weight: 400;
          margin: 0;
          padding: 0;
          line-height: 14px;
        }

        .items {
          margin-top: 75px;
        }

        .timer {
          height: 12px;
        }
      }

      &.common {
        background: #b4e56f;
        border: $store-pack-border-width solid #daf5ab;

        .sub-header {
          background: $commonMainColor;
          background: radial-gradient(
                          ellipse at center,
                          #d4dd1e 10%,
                          color.adjust($commonMainColor, $saturation: 40%) 100%
          );
        }
      }

      &.rare {
        background: #76cdfe;
        border: $store-pack-border-width solid #a1f2f5;

        .sub-header {
          background: $rareMainColor;
          background: radial-gradient(
                          ellipse at center,
                          #23b6da 10%,
                          color.adjust($rareMainColor, $saturation: 40%) 100%
          );
        }
      }

      &.epic {
        background: #f4c7ff;
        border: $store-pack-border-width solid #ffdbff;

        .sub-header {
          background: $epicMainColor;
          background: radial-gradient(
                          ellipse at center,
                          #ff5acf 10%,
                          color.adjust($epicMainColor, $saturation: 40%) 100%
          );
        }
      }

      &.legendary {
        background: #ffdc6a;
        border: $store-pack-border-width solid #f7e3ad;

        .sub-header {
          background: $legendaryMainColor;
          background: radial-gradient(
                          ellipse at center,
                          #ffb92d 10%,
                          color.adjust($legendaryMainColor, $saturation: 40%) 100%
          );
        }
      }

      .header {
        position: absolute;
        top: -$store-pack-border-width;
        left: -$store-pack-border-width;
        right: -$store-pack-border-width;
        height: $store-pack-header-height;
        overflow: hidden;
        box-shadow: inset 0 1px 18px 0 #ffffff;
        border-radius: $store-pack-border-radius $store-pack-border-radius 0 0;
        background: $dark-color;

        @keyframes fadeInOut {
          0%, 100% {
            opacity: 0.2;
          }
          50% {
            opacity: 0.8;
          }
        }

        .shadow {
          display: block;
          height: 100%;
          position: absolute;
          top: 0;
          width: 100%;
          box-shadow: inset 0 5px 15px 0 #ffffff,
          inset 0px 1px 0px 0px rgb(255 255 255 / 65%);
          z-index: 3;
          border-radius: $store-pack-border-radius $store-pack-border-radius 0 0;
          background-size: cover;
        }

        :global(.winter) & .shadow {
          background-image: url("/images/frost.png");
          animation: fadeInOut 10s infinite;
        }

        .pack-image {
          position: relative;
          top: 0;
          left: 0;
          right: 0;
          z-index: 2;
          background: color.adjust($dark-color, $lightness: -2%);
          object-fit: cover;
          object-position: center top;
          width: 100%;
          height: $store-pack-header-height;
        }

        h2 {
          z-index: 35;
          position: absolute;
          font-weight: 700;
          font-family: var(--font-family-sans-condensed);
          text-align: center;
          left: 0;
          right: 0;
          bottom: 10px;
          padding: 0 15px;
          margin: 0;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7), 0 1px 0 rgba(0, 0, 0, 0.3);
          font-size: 16px;
          line-height: 20px;
          cursor: default;
        }
      }

      .sub-header {
        position: absolute;
        top: $store-pack-header-height - $store-pack-border-width;
        left: -$store-pack-border-width - 3;
        right: -$store-pack-border-width - 3;
        overflow: hidden;
        height: 60px;
        text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.4);
        box-shadow: 0 5px 30px 0 rgb(0 0 0 / 35%),
        inset 0 0 22px 0 rgb(0 23 53 / 32%),
        inset 0px 1px 0px 0px rgb(255 255 255 / 45%),
        inset 0px -2px 2px 1px rgb(0 0 0 / 45%);
        border-radius: 3px;
        padding: 0 $store-pack-border-width * 4 - 5;
        text-align: center;
        font-family: var(--font-family-sans-condensed);
        display: flex;
        flex-direction: column;
        align-content: center;
        justify-content: center;
        align-items: center;
        z-index: 15;
        cursor: default;

        h3 {
          font-family: var(--font-family-sans-condensed);
          font-size: 18px;
          line-height: 24px;
          font-weight: 700;
          margin: 0;
          color: #feffd6;
        }

        p {
          font-size: 12px;
          font-weight: 400;
          margin: 0;
          padding: 0;
          line-height: 14px;
        }

      }
    }

    .resources {
      .claim-button {
        width: 100%;
        align-self: flex-end;
        z-index: 15 !important;
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        padding: 1rem 2.5rem !important;
      }
    }

    .buy-button {
      width: 100%;
      align-self: flex-end;
      z-index: 15 !important;
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 1rem 2.5rem !important;

      s,
      strike {
        font-size: 14px;
        text-decoration: none;
        position: relative;
        color: rgba(0, 0, 0, 0.65);

        &:before {
          top: 50%; /*tweak this to adjust the vertical position if it's off a bit due to your font family */
          background: red; /*this is the color of the line*/
          opacity: 0.7;
          content: '';
          width: 110%;
          position: absolute;
          height: 0.1em;
          border-radius: 0.1em;
          left: -5%;
          white-space: nowrap;
          display: block;
          transform: rotate(-15deg);
        }

        &.straight::before {
          transform: rotate(0deg);
          left: -1%;
          width: 102%;
        }
      }
    }

    .packGameToken {
      &:after {
        content: "";
        background: url('/images/items/packGameToken.png');
        width: 40px;
        height: 40px;
        background-size: cover;
        position: relative;
        bottom: 1px;
      }
    }
  }

  // Compact mobile card styles are shared with SubscriptionPack via this mixin
  // (rendered only on mobile; desktop never receives `.pack-compact` markup).
  @include compact.compact-card-styles;
</style>
